
import React, { createContext, useContext, useEffect, useState } from 'react';
import { synczbase } from '../lib/synczbase';
import { getSuperAdminEmails } from '../utils/envAuth';
import { Loader2, AlertTriangle } from 'lucide-react';
import { TenantSubscription, SaaSPlan, SaaSFeatureKey } from '../types/saas';
import { useParams, useLocation } from 'react-router-dom';
import { DOMAIN_CONFIG, IS_LOCALHOST, isSaaSPlatform, isStandalonePortfolio } from '../config/domainConfig';

interface Tenant {
    id: string;
    name: string;
    domains: string[]; 
    customDomain?: string;
    subdomain?: string;
    ownerId: string;
    subscriptionStatus: 'ACTIVE' | 'SUSPENDED'; 
    subscription?: TenantSubscription; 
    config: any; 
    timezone?: string;
    isRoot?: boolean; 
    industry?: string;
}

interface TenantContextType {
    tenant: Tenant | null;
    currentPlan: SaaSPlan | null;
    loading: boolean;
    error: string | null;
    isPlatformAdmin: boolean;
    hasFeature: (feature: SaaSFeatureKey) => boolean;
}

const TenantContext = createContext<TenantContextType>({
    tenant: null,
    currentPlan: null,
    loading: true,
    error: null,
    isPlatformAdmin: false,
    hasFeature: () => false
});

export const useTenant = () => useContext(TenantContext);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const params = useParams();
    const location = useLocation();
    const hostname = window.location.hostname;
    
    // In-memory tenant state queried live from PostgreSQL
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const [currentPlan, setCurrentPlan] = useState<SaaSPlan | null>(null);
    const [platformRefresh, setPlatformRefresh] = useState(0);

    // Listen for platform settings updates (e.g. changing Default Portfolio Owner)
    useEffect(() => {
        const handlePlatformUpdate = () => {
            setPlatformRefresh(prev => prev + 1);
        };
        window.addEventListener('syncz_platform_updated', handlePlatformUpdate);
        return () => window.removeEventListener('syncz_platform_updated', handlePlatformUpdate);
    }, []);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [referralCode, setReferralCode] = useState<string | null>(null);
    
    const isHardcodedAdmin = isSaaSPlatform(hostname);

    // Capture affiliate/referral parameters in-memory
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const ref = queryParams.get('ref') || queryParams.get('affiliate') || queryParams.get('referral');
        if (ref) {
            setReferralCode(ref);
            console.log('[SyncZ Affiliate] Captured referral code:', ref);
        }
    }, [location]);

    useEffect(() => {
        let isMounted = true;
        
        const resolveTenant = async () => {
            // Only set loading true if we don't have a cache to show
            if (!tenant) setLoading(true);
            setError(null);

            let resolvedTenant: Tenant | null = null;
            let tenantId = '';

            // 1. PATH-BASED TENANT (/t/slug)
            let pathSlug = params.tenantSlug;
            if (!pathSlug) {
                const match = location.pathname.match(/^\/t\/([^/]+)/);
                if (match) pathSlug = match[1];
            }

            if (pathSlug) {
                try {
                    const snap = await synczbase.query("SELECT * FROM tenants WHERE \"subdomain\" = $1 LIMIT 1", [pathSlug]);
                    if (snap && snap.length > 0) {
                        resolvedTenant = snap[0] as unknown as Tenant;
                        tenantId = snap[0].id;
                    } else {
                         if (isMounted) setError('Workspace not found.');
                         if (isMounted) setLoading(false);
                         return;
                    }
                } catch (e) { console.error(e); }
            }
            
            // 2. DOMAIN-BASED TENANT (subdomain.app.com or custom.com)
            const isRoot = hostname === DOMAIN_CONFIG.SAAS_ROOT || hostname === `www.${DOMAIN_CONFIG.SAAS_ROOT}` || IS_LOCALHOST;

            if (!resolvedTenant && !isRoot && !isHardcodedAdmin) {
                 try {
                     let queryDomain = hostname;
                     let snapshot = await synczbase.query("SELECT * FROM tenants WHERE $1 = ANY(domains) LIMIT 1", [queryDomain]);
                     
                     if (!snapshot || snapshot.length === 0) {
                         snapshot = await synczbase.query("SELECT * FROM tenants WHERE \"customDomain\" = $1 LIMIT 1", [queryDomain]);
                     }

                     if ((!snapshot || snapshot.length === 0) && isStandalonePortfolio(hostname)) {
                         if (DOMAIN_CONFIG.STANDALONE_TENANT_ID) {
                             snapshot = await synczbase.query("SELECT * FROM tenants WHERE id = $1 OR subdomain = $1 LIMIT 1", [DOMAIN_CONFIG.STANDALONE_TENANT_ID]);
                         }
                         if (!snapshot || snapshot.length === 0) {
                             snapshot = await synczbase.query("SELECT * FROM tenants WHERE status = 'ACTIVE' ORDER BY \"createdAt\" ASC LIMIT 1");
                         }
                     }

                    if (snapshot && snapshot.length > 0) {
                        resolvedTenant = snapshot[0] as unknown as Tenant;
                        tenantId = snapshot[0].id;
                    } 
               } catch (e: any) { 
                   if (e?.code !== 'permission-denied') {
                       console.warn("Tenant resolution check failed", e);
                   }
               }
           }

            // 3. ROOT DOMAIN / DEFAULT TENANT
            if (!resolvedTenant) {
                try {
                    let ownerIdToUse = '';
                    
                    try {
                        const platformSettings = await synczbase.query("SELECT * FROM settings WHERE id = 'platform' LIMIT 1");
                        if (platformSettings && platformSettings.length > 0) {
                            const row = platformSettings[0] as any;
                            let dataObj: any = row;
                            if (typeof row.data === 'string') {
                                try { dataObj = JSON.parse(row.data); } catch(e) {}
                            } else if (row.data && typeof row.data === 'object') {
                                dataObj = row.data;
                            }
                            if (dataObj && dataObj.defaultPortfolioOwnerId) {
                                ownerIdToUse = dataObj.defaultPortfolioOwnerId;
                            }
                        }
                    } catch(e) {}

                    if (!ownerIdToUse) {
                        try {
                            const superSnap = await synczbase.query("SELECT * FROM users WHERE role = 'SUPER_ADMIN' LIMIT 1");
                            if (superSnap && superSnap.length > 0) ownerIdToUse = superSnap[0].id || (superSnap[0] as any).uid;
                        } catch (e) {}
                    }

                    let ownerName = 'SyncZ Workspace';
                    if (ownerIdToUse) {
                        try {
                            const ownerSnap = await synczbase.query("SELECT * FROM users WHERE id = $1 or uid = $1 LIMIT 1", [ownerIdToUse]);
                            const ownerData = ownerSnap && ownerSnap.length > 0 ? ownerSnap[0] as any : null;
                            if (ownerData) {
                                ownerName = ownerData.displayName || ownerData.name || ownerData.invoiceDetails?.businessName || ownerName;
                            }
                        } catch (e) {}
                    }

                    resolvedTenant = {
                        id: 'root_platform',
                        name: ownerName,
                        domains: [hostname],
                        ownerId: ownerIdToUse || 'root_owner',
                        subscriptionStatus: 'ACTIVE',
                        subdomain: 'root',
                        isRoot: true,
                        config: {}
                    };
                    tenantId = 'root_platform';

                    if (ownerIdToUse) {
                        try {
                            const portfolioSnap = await synczbase.query("SELECT * FROM portfolios WHERE \"ownerId\" = $1 OR id = $1 LIMIT 1", [ownerIdToUse]);
                            if (portfolioSnap && portfolioSnap.length > 0) {
                                const sData = portfolioSnap[0] as any;
                                resolvedTenant.name = sData?.name || resolvedTenant.name;
                            }
                        } catch (e) {}
                    }
                } catch (e) { console.error("Root tenant resolution error:", e); }
            }

            if (isMounted) {
                if (resolvedTenant) {
                    if (resolvedTenant.subscriptionStatus === 'SUSPENDED') {
                        setError('This account has been suspended.');
                    } else {
                        const finalTenant = { ...resolvedTenant, id: tenantId };
                        // Compare with cache to avoid unnecessary re-renders
                        if (JSON.stringify(finalTenant) !== JSON.stringify(tenant)) {
                            setTenant(finalTenant);
                        }
                    }
                } else {
                    setTenant(null);
                }
                setLoading(false);
            }
        };

        resolveTenant();

        return () => { isMounted = false; };
    }, [hostname, location.pathname, params.tenantSlug, platformRefresh]);

    // Fetch Plan Details when Tenant Changes
    useEffect(() => {
        const fetchPlan = async () => {
            if (tenant?.subscription?.planId) {
                try {
                    const planSnap = await synczbase.query("SELECT * FROM saas_plans WHERE id = $1 LIMIT 1", [tenant.subscription.planId]);
                    if (planSnap && planSnap.length > 0) {
                        setCurrentPlan(planSnap[0] as unknown as SaaSPlan);
                    }
                } catch (e) { console.error("Failed to load plan", e); }
            } else {
                setCurrentPlan(null);
            }
        };
        fetchPlan();
    }, [tenant?.subscription?.planId]);

    const hasFeature = (feature: SaaSFeatureKey): boolean => {
        if (tenant?.isRoot) return true;
        if (!currentPlan) return false;
        return currentPlan.features.includes(feature);
    };

    const [authUser, setAuthUser] = useState<any>(null);
    useEffect(() => {
        if (synczbase.getToken()) {
            synczbase.apiCall('/api/auth/me', 'GET').then(setAuthUser).catch(() => {});
        }
    }, [tenant?.id]);
    const isPlatformAdmin = authUser?.id === tenant?.ownerId || false;

    return (
        <TenantContext.Provider value={{ tenant, currentPlan, loading, error, isPlatformAdmin, hasFeature }}>
            {children}
        </TenantContext.Provider>
    );
};
